import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../../utils/toast';
import { track, identifyUser } from '../../analytics/track';
import { EVENTS } from '../../analytics/events';
import { LoginView } from './login.view';

/**
 * Login, the container. Routing, auth, analytics, the attempt counter.
 *
 * Everything visual lives in `login.view.jsx`, which is pure, so the state
 * explorer can mount the real screen without any of this running. See
 * `design-system/explorer/registry.js` for that convention.
 *
 * This page no longer emits a single class name from `login.css` and no longer
 * imports it. The file itself cannot go yet: `Signup`, `LandingPage` and
 * `EmailVerification` all import it too, which is exactly the coupling this
 * conversion exists to remove. It goes when the last of them is converted.
 */

const LOGIN_ATTEMPT_KEY = 'vaultsa_login_attempts';

export default function Login() {
  const { login, isAuthenticated, loading, error, message } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/marketplace';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    const result = await login(values.email, values.password);

    if (result.success) {
      localStorage.removeItem(LOGIN_ATTEMPT_KEY);
      identifyUser(result.user);
      track(EVENTS.LOGIN_SUCCESS, {
        user_id: result.user?.id,
        method: 'email',
      });
      showToast.success(result.message || 'Login successful');

      /* WHERE THEY WERE GOING. `ProtectedRoute` bounces here with
         `state={{ from: location }}` and the effect above honours it, but only
         for someone who arrives ALREADY signed in. This branch hard-coded
         `/marketplace`, so an actual sign-in threw the destination away: a
         buyer bounced off `/checkout` was dropped on the marketplace with no
         mention of the cart they were three clicks into paying for.

         An admin is the exception. A buyer-side `from` is not somewhere they
         were trying to go, it is wherever they happened to click, so the
         console still wins. */
      const userRole = result.user?.role?.toLowerCase();
      const from = location.state?.from?.pathname;
      const redirectPath =
        userRole === 'admin' ? '/admin-dashboard' : from || '/marketplace';

      navigate(redirectPath, { replace: true });
    } else {
      const attempts = (parseInt(localStorage.getItem(LOGIN_ATTEMPT_KEY) || '0', 10) || 0) + 1;
      localStorage.setItem(LOGIN_ATTEMPT_KEY, String(attempts));
      let failureReason = 'invalid_credentials';
      const msg = (result.error || result.message || '').toLowerCase();
      if (msg.includes('not found') || msg.includes('no account')) failureReason = 'user_not_found';
      else if (msg.includes('locked')) failureReason = 'account_locked';
      else if (msg.includes('verify')) failureReason = 'email_unverified';
      track(EVENTS.LOGIN_FAILED, {
        failure_reason: failureReason,
        attempt_count: attempts,
        is_2fa_required: false,
        is_authenticated: false,
      });

      if (
        result.error?.includes('verify your email') ||
        result.message?.includes('verify your email')
      ) {
        showToast.info('Please verify your email before logging in');
        navigate(`/email-verification-otp?email=${encodeURIComponent(values.email)}`, {
          replace: true,
        });
        return;
      }

      // Handle specific field errors if backend provides them
      if (result.error?.toLowerCase().includes('email')) {
        setFieldError('email', result.error);
      } else if (result.error?.toLowerCase().includes('password')) {
        setFieldError('password', result.error);
      }
    }

    setSubmitting(false);
  };

  return (
    <LoginView
      onSubmit={handleSubmit}
      loading={loading}
      error={error}
      message={message}
      /* So the page can say why they are here, and point back at the cart
         rather than the marketplace. Same value `handleSubmit` returns to. */
      returningTo={location.state?.from?.pathname}
      returnReason={location.state?.reason}
    />
  );
}
